import axios from "axios";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

function Signin() {
    const navigate = useNavigate();

    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");

    async function signin() {
        setError("");
        setLoading(true);
        try {
            const apiUrl = "https://matrix-sggb.onrender.com";
            const response = await axios.post(`${apiUrl}/auth/signin`, {
                email,
                password,
            });
            if (response.status === 200) {
                const token = response.data.token;
                localStorage.setItem("token", token);
                navigate("/home");
            } else {
                setError(response.data.message || "Something went wrong.");
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || "Signin failed. Please try again.");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-[100dvh] bg-[var(--auth-bg)] flex flex-col items-center justify-center p-6 sm:p-8 relative overflow-hidden">
            {/* Ambient background orbs */}
            <div className="absolute rounded-full blur-[80px] pointer-events-none animate-[float-orb_8s_ease-in-out_infinite] w-[280px] h-[280px] sm:w-[500px] sm:h-[500px] -top-[120px] -left-[120px] sm:-top-[200px] sm:-left-[200px] bg-[radial-gradient(circle,rgba(139,92,246,0.3)_0%,transparent_70%)]" />
            <div className="absolute rounded-full blur-[80px] pointer-events-none animate-[float-orb_8s_ease-in-out_infinite] w-[220px] h-[220px] sm:w-[400px] sm:h-[400px] -bottom-[80px] -right-[60px] sm:-bottom-[150px] sm:-right-[100px] bg-[radial-gradient(circle,rgba(99,102,241,0.25)_0%,transparent_70%)] [animation-delay:-4s]" />
            <div className="absolute rounded-full blur-[80px] pointer-events-none animate-[float-orb_8s_ease-in-out_infinite] w-[160px] h-[160px] sm:w-[300px] sm:h-[300px] top-[50%] left-[60%] bg-[radial-gradient(circle,rgba(168,85,247,0.2)_0%,transparent_70%)] [animation-delay:-2s]" />
            <div className="absolute inset-0 bg-[linear-gradient(rgba(139,92,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(139,92,246,0.03)_1px,transparent_1px)] bg-[length:60px_60px] pointer-events-none" />

            <div className="w-full max-[480px]:max-w-full max-w-[440px] animate-[fade-in-up_0.6s_ease_both]">
                <div className="bg-[var(--auth-card)] backdrop-blur-xl border border-[var(--auth-border)] rounded-[20px] sm:rounded-3xl p-8 px-5 sm:px-9 sm:py-9 relative z-10 shadow-[0_0_0_1px_rgba(139,92,246,0.1),0_24px_64px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.05)] before:absolute before:-inset-[1px] before:rounded-[21px] sm:before:rounded-[25px] before:bg-gradient-to-br before:from-violet-500/30 before:via-transparent before:to-indigo-500/20 before:z-[-1] before:pointer-events-none">
                    {/* Logo */}
                    <div className="flex items-center justify-center gap-3 mb-5 sm:mb-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-violet-500 to-indigo-500 rounded-xl sm:rounded-2xl flex items-center justify-center text-[18px] sm:text-[22px] font-extrabold text-white shadow-[0_8px_24px_rgba(139,92,246,0.5)] animate-[pulse-glow_3s_ease-in-out_infinite]">M</div>
                        <span className="text-[22px] sm:text-[26px] font-extrabold bg-gradient-to-br from-violet-300 to-indigo-100 bg-clip-text text-transparent tracking-tight">Matrix</span>
                    </div>

                    <h1 className="text-[22px] sm:text-[28px] font-bold text-[var(--auth-text)] text-center mb-1 tracking-tight">Sign in to Matrix</h1>
                    <p className="text-[13px] sm:text-sm text-[var(--auth-muted)] text-center mb-5">Pick up where you left off. Your ranking awaits.</p>

                    {/* Error */}
                    {error && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-[10px] p-2.5 px-3.5 mb-3.5 sm:mb-4 text-red-300 text-[13px] text-center">
                            {error}
                        </div>
                    )}

                    {/* Form */}
                    <div className="mb-3.5">
                        <label className="block text-[13px] font-medium text-violet-300 mb-2 tracking-wide">Email address</label>
                        <input
                            id="signin-email"
                            className="w-full p-[11px] px-3.5 sm:px-4 sm:p-[13px] bg-[var(--auth-input-bg)] border border-[var(--auth-input-border)] rounded-xl text-[16px] sm:text-[15px] font-sans text-[var(--auth-text)] outline-none transition-all duration-300 placeholder:text-gray-400/50 focus:border-[var(--auth-input-focus)] focus:bg-[rgba(139,92,246,0.08)] focus:shadow-[0_0_0_3px_rgba(139,92,246,0.15),0_4px_16px_rgba(0,0,0,0.2)] caret-violet-500"
                            type="email"
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                        />
                    </div>

                    <div className="mb-3.5">
                        <label className="block text-[13px] font-medium text-violet-300 mb-2 tracking-wide">Password</label>
                        <input
                            id="signin-password"
                            className="w-full p-[11px] px-3.5 sm:px-4 sm:p-[13px] bg-[var(--auth-input-bg)] border border-[var(--auth-input-border)] rounded-xl text-[16px] sm:text-[15px] font-sans text-[var(--auth-text)] outline-none transition-all duration-300 placeholder:text-gray-400/50 focus:border-[var(--auth-input-focus)] focus:bg-[rgba(139,92,246,0.08)] focus:shadow-[0_0_0_3px_rgba(139,92,246,0.15),0_4px_16px_rgba(0,0,0,0.2)] caret-violet-500"
                            type="password"
                            placeholder="Your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                            onKeyDown={(e) => e.key === "Enter" && signin()}
                        />
                    </div>

                    <button
                        id="signin-submit"
                        className="w-full p-[13px] sm:p-[14px] mt-2 bg-gradient-to-br from-violet-500 to-indigo-500 text-white text-[15px] font-semibold font-sans rounded-xl cursor-pointer relative overflow-hidden transition-all duration-300 shadow-[0_8px_24px_rgba(139,92,246,0.4)] tracking-wide hover:-translate-y-0.5 hover:shadow-[0_12px_32px_rgba(139,92,246,0.55)] active:translate-y-0 active:shadow-[0_4px_12px_rgba(139,92,246,0.3)] before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/15 before:to-transparent before:pointer-events-none"
                        onClick={signin}
                        disabled={loading}
                    >
                        {loading ? "Signing in…" : "Sign In →"}
                    </button>

                    <div className="flex items-center gap-3 my-[18px] sm:my-6">
                        <div className="flex-1 h-px bg-violet-500/20" />
                        <span className="text-xs font-medium text-[var(--auth-muted)] uppercase tracking-widest">or</span>
                        <div className="flex-1 h-px bg-violet-500/20" />
                    </div>

                    <div className="text-center text-sm text-[var(--auth-muted)]">
                        Don't have an account?
                        <button
                            id="goto-signup"
                            className="inline border-none outline-none bg-transparent text-violet-400 font-semibold cursor-pointer text-sm font-sans ml-1 transition-colors hover:text-violet-300 hover:underline hover:underline-offset-2 p-0"
                            onClick={() => navigate("/signup")}
                        >
                            Sign up
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Signin;